import { CodeBlock, CopyBlock, monokai } from "react-code-blocks";
import { Link, useOutletContext } from "react-router-dom";
import {
    errorMessageV3,
    marketDataStreamStartAndUpdateMessages,
    startMarketDataStreamMessage, startMarketDataStreamMessageWithAllFields,
    stopAcknowledgmentMessageV3,
    stopStreamMessageV3,
    streamingURLV3
} from "../Assets/apiDefinitionExamples";
import { APINames, PageNames, TabNames } from "../Assets/variables";
import { useEffect } from "react";

const APIDefinitionStreamingMarketDataV3 = () => {
    const {setActiveTabItem} = useOutletContext();

    useEffect(() => {
        setActiveTabItem(TabNames.APIDefinition);
    }, []);

    return (
        <div id="tab-definition" class="api-panel">
            <div className="api-definition-container">
                <p>Streaming Market Data API will provide streaming access to latest prices through a web socket connection.</p>
                <p>Details on getting started with a streaming client can be found in our <Link to={`/${PageNames.Documentation}#web-socket-client-example`}>documentation</Link>. You can also try the protocol directly in the <strong>Websocket Explorer</strong> tab of this page.</p>

                <p>The full API definition is described as an AsyncAPI 3.0 specification.
                    Download the specification:&nbsp;&nbsp;
                    <a href="/portal/bfi/V3/streamingMarketData.json" download="streamingMarketData.json" target="_blank" rel="noopener noreferrer">JSON</a>
                    &nbsp;|&nbsp;
                    <a href="/portal/bfi/V3/streamingMarketData.yml" download="streamingMarketData.yml" target="_blank" rel="noopener noreferrer">YML</a>.
                </p>

                <span className="heading-2">Streaming URL</span>
                <p>Below is the web socket URL for the Streaming Market Data V3 API.</p>

                <div className="code-example">
                    <CopyBlock
                        text={streamingURLV3}
                        language={"text"}
                        showLineNumbers={false}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <span className="heading-2">Start Stream</span>
                <p>You can start the stream by sending a <strong>startMarketDataStream</strong> message.</p>
                <p>Below is an example of a <strong>startMarketDataStream</strong> message that requests the best bid and best ask for a listing, conflated every 2 seconds</p>

                <div className="code-example">
                    <CopyBlock
                        text={startMarketDataStreamMessage}
                        language={"json"}
                        showLineNumbers={true}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <p>The responses will have a START message followed by UPDATE messages.</p>

                <div className="code-example">
                    <CodeBlock
                        text={marketDataStreamStartAndUpdateMessages}
                        language={"text"}
                        showLineNumbers={true}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <span className="heading-3">Start Stream Parameters</span>

                <table>
                    <thead>
                        <tr>
                            <th>Parameter</th>
                            <th>Type</th>
                            <th>Available Values</th>
                            <th>Example</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>streamId*</td>
                            <td>string</td>
                            <td></td>
                            <td>MarketDataStreamId-001</td>
                            <td>The Stream ID identifies the stream. It is used to close a specific stream, and is echoed back on every message for that stream.</td>
                        </tr>
                        <tr>
                            <td>scheme*</td>
                            <td>enum string</td>
                            <td>VALOR_BC, ISIN_BC, CUSIP_BC, SEDOL_BC, TICKER_BC, WKN_BC, COMMONCODE_BC, FIGI_BC, BBGTICKER_BC, and the _MIC / _MIC_CCY variants of each</td>
                            <td>VALOR_BC</td>
                            <td>Scheme of the Listing Identifier. It is a combination of the instrument scheme and the market scheme.</td>
                        </tr>
                        <tr>
                            <td>id*</td>
                            <td>string</td>
                            <td></td>
                            <td>1222171_4</td>
                            <td>Listing Identifier to be provided by client as input parameter.</td>
                        </tr>
                        <tr>
                            <td>qualityOfService*</td>
                            <td>enum string</td>
                            <td>REAL_TIME, DELAYED, END_OF_DAY, PREVIOUS_DAY</td>
                            <td>REAL_TIME</td>
                            <td>The quality of service requested for the stream.</td>
                        </tr>
                        <tr>
                            <td>conflationType</td>
                            <td>enum string</td>
                            <td>NO_CONFLATION, INTERVAL</td>
                            <td>INTERVAL</td>
                            <td>Conflation Type is an optional parameter. By default all messages are sent without any conflation. For interval conflation, you have to provide the conflation period as a string in ISO 8601 duration format in the input parameter conflationPeriod.</td>
                        </tr>
                        <tr>
                            <td>conflationPeriod</td>
                            <td>string</td>
                            <td></td>
                            <td>PT30S</td>
                            <td>
                                <p>Provided as string in ISO 8601 duration format. The minimum conflation period possible is 100 milliseconds and the maximum being 1 minute. Any period shorter than 1 minute ("PT1M") has to be requested in seconds (e.g. "PT30S"). Examples are:</p>
                                <ul>
                                    <li>PT1M = 1 minute</li>
                                    <li>PT1S = 1 second</li>
                                    <li>PT0.5S = 500 milliseconds</li>
                                    <li>PT0.1S = 100 milliseconds</li>
                                </ul>
                            </td>
                        </tr>
                        <tr>
                            <td>fields</td>
                            <td>array[enum string]</td>
                            <td></td>
                            <td>["bestBid", "bestAsk", "tradingEvent"]</td>
                            <td>Fields are the prices that are available in the API. For list of Fields available and the descriptions, refer to <Link to={`/${PageNames.Catalog}/${APINames.streamingMarketDataV3}/${TabNames.Content}`}>fields</Link></td>
                        </tr>
                    </tbody>
                </table>

                <span className="heading-3">Response Fields</span>
                <p>Quote fields you subscribed to via <strong>fields</strong> are delivered as top-level fields on the message, using the same camelCase name you requested (e.g. a <strong>bestAsk</strong> quote is returned as a <strong>bestAsk</strong> object with <strong>value</strong>, <strong>size</strong> and <strong>unixTimestamp</strong>).</p>

                <table>
                    <thead>
                        <tr>
                            <th>Field Name</th>
                            <th>Sub Fields</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>type</td>
                            <td></td>
                            <td>
                                Type of the message:
                                <ul>
                                    <li><strong>START</strong>: the initial snapshot delivered right after a successful <strong>startMarketDataStream</strong>. All requested quote fields and reference data are included.</li>
                                    <li><strong>UPDATE</strong>: a subsequent update. Only the quote fields that changed are included.</li>
                                    <li><strong>STOP</strong>: acknowledges a <strong>stop</strong> message and represents the end of the stream.</li>
                                </ul>
                            </td>
                        </tr>
                        <tr>
                            <td>streamId</td>
                            <td>string</td>
                            <td>Echoes the streamId provided in the request, so messages from multiple concurrent streams on the same WebSocket connection can be told apart.</td>
                        </tr>
                        <tr>
                            <td>qualityOfService</td>
                            <td></td>
                            <td>Only present on the START message. Echoes the quality of service used for the stream.</td>
                        </tr>
                        <tr>
                            <td rowSpan={5}>listingLookup</td>
                            <td></td>
                            <td>Only present on the START message. Provides key reference data fields about the listing.</td>
                        </tr>
                        <tr>
                            <td>bc</td>
                            <td>SIX market identifier (BC) for the listing.</td>
                        </tr>
                        <tr>
                            <td>listingShortName</td>
                            <td>Listing short name.</td>
                        </tr>
                        <tr>
                            <td>marketShortName</td>
                            <td>Market short name.</td>
                        </tr>
                        <tr>
                            <td>listingCurrency</td>
                            <td>The trading currency according to ISO 4217 alpha codes.</td>
                        </tr>
                        <tr>
                            <td rowSpan={2}>tradingEvent</td>
                            <td></td>
                            <td>Is updated when there is a change in <strong>tradingEvent</strong></td>
                        </tr>
                        <tr>
                            <td>category, unixTimestamp</td>
                            <td>Category of the trading event (e.g. AUCTION, CONTINUOUS_TRADING, TRADE_HALTED, CLOSED_MARKET) and the Unix timestamp (epoch seconds) it occurred at.</td>
                        </tr>
                    </tbody>
                </table>

                <span className="heading-2">Stop Stream</span>
                <p>In order to close a stream, send the below <strong>stop</strong> message with the streamId.</p>

                <div className="code-example">
                    <CopyBlock
                        text={stopStreamMessageV3}
                        language={"json"}
                        showLineNumbers={true}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <p>You will receive a STOP message upon closing the stream</p>

                <div className="code-example">
                    <CodeBlock
                        text={stopAcknowledgmentMessageV3}
                        language={"text"}
                        showLineNumbers={false}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <span className="heading-2">Error Handling</span>
                <p>If a request cannot be processed, an ERROR message is sent instead, for example when stopping a streamId that does not exist or has already been stopped:</p>

                <div className="code-example">
                    <CodeBlock
                        text={errorMessageV3}
                        language={"text"}
                        showLineNumbers={false}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <span className="heading-2">All fields example</span>
                <p>Example below show all fields possible to request</p>

                <div className="code-example">
                    <CopyBlock
                        text={startMarketDataStreamMessageWithAllFields}
                        language={"text"}
                        showLineNumbers={false}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>
            </div>
        </div>
    );
}

export default APIDefinitionStreamingMarketDataV3;
